/**
 * The evidence modal's reading layer (K5, ND-05 / audit S2-01): readable English column labels
 * with their unit, identifiers shown as text, the KPI's own columns first, and an English
 * description per KPI in place of the engine's German one. Pure functions, unit-tested. The rows
 * and their values are the engine's; nothing here computes a figure.
 *
 * B2 (masterplan skpi-redesign, E9) adds, below the original helpers (which are unchanged): the node an
 * evidence view is about (`resolveEvidenceNode`, `evidenceQuery`, `rowsAvailability`), the figures of the
 * side panel read out of the actuals payload (`evidencePanelModel`, no request), and the pure formatters of
 * the rows view that the modal keeps privately (`summaryLabel`, `summaryValue`, `evidenceCellText`).
 */
import type {
    ActualsResponse, EvidenceNode, EvidenceQuery, EvidenceScopeType, GroupActuals, GroupScopeType, KpiCatalogueEntry,
    KpiSelection, PersonActuals,
} from '../types/strategicKpis';
import { formatAchievement, formatKpiValue, judgedOnYtdShare, kpiLabel, noDataText, paceBasisText, STATUS_LABEL, windowText, type Light } from './strategicKpiFormat';
import { formatDate, formatDateTimeCH } from './format';
import { judgeGroup, judgePerson, lightOf, paceAtYearEnd, type JudgedValue } from './strategicKpiSelectors';

/** Column name of the evidence endpoint → what the reader sees. Unknown columns pass through. */
export const COLUMN_LABEL: Record<string, string> = {
    // CRM deals
    Person: 'Person', DealId: 'Deal ID', DealName: 'Deal', StageLabel: 'Stage', IsClosed: 'Closed', IsWon: 'Won',
    CreateDate: 'Created', CloseDate: 'Close date', ActualCloseDate: 'Won / lost on',
    RelevantDays: 'Days in relevant stages', DaysToClose: 'Days created → close date',
    ReachedTrialOrNegotiation: 'Reached trial / negotiation', PotentialGpPerYear: 'Potential GP per year (EUR)',
    GreenProjectFlag: 'Green project', DealUrl: 'HubSpot',
    // Visits
    EngagementId: 'Meeting ID', MeetingDate: 'Date', StartTime: 'Start', ActivityType: 'Activity type',
    ActivityClass: 'Activity', Classification: 'Class', CustomerID: 'Customer no.', CustomerName: 'Customer',
    Title: 'Title', MeetingURL: 'HubSpot',
    // Gross profit
    CompanyCode: 'Company', PostingDate: 'Posting date', DocumentCode: 'Document', DocumentLineNr: 'Line no.',
    SalesManagerName: 'Sales manager', ProductID: 'Product no.', ProductName: 'Product',
    GrossProfitEUR: 'Gross profit (EUR)', NetSalesEUR: 'Net sales (EUR)', BaseCategoryName: 'Category',
    SubregionName: 'Subregion', IsGreen: 'Green', IsCosmetics: 'Cosmetics', IsAdhesive: 'Adhesive',
    IsOwnBrand: 'Own brand', IsNorthAfrica: 'North Africa', IsCcTurkey: 'C&C Turkey',
    TransactionSupplierID: 'Supplier code', SupplierName: 'Supplier', IsKraton: 'Kraton',
    // BAB
    BuCode: 'BU', Year: 'Year', Month: 'Month', LineCode: 'BAB line', LineName: 'Line',
    AmountEUR: 'Amount as booked (EUR)', Signage: 'Sign', Amount: 'Amount (EUR)', LoadTimeStamp: 'Frozen at',
    // Market data
    MarketdataId: 'Record ID', Name: 'Name', Company: 'Company', Vendor: 'Vendor', Category: 'Category',
    CreatedAt: 'Created', ModifiedAt: 'Modified', Active: 'Active',
};

export function columnLabel(col: string): string {
    return COLUMN_LABEL[col] ?? col.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** Identifiers are text: no grouping ("40'000" was a line number), left-aligned, never summed. */
export function isIdentifierColumn(col: string): boolean {
    return /(?:Id|ID|Nr|Code)$/.test(col) || col === 'Year' || col === 'Month';
}

/** Amounts in EUR: two decimals, so a column of them lines up. */
export function isAmountColumn(col: string): boolean {
    return /EUR$/.test(col) || col === 'Amount' || col === 'PotentialGpPerYear';
}

/** Day counts: one decimal, as on the tile. */
export function isDaysColumn(col: string): boolean {
    return col === 'RelevantDays' || col === 'DaysToClose';
}

/** The flag column a GP KPI filters on — shown right after GP, supplier and Kraton. */
const GP_FLAG: Record<string, string> = {
    'gp.share_green': 'IsGreen', 'gp.share_cosmetics': 'IsCosmetics', 'gp.share_adhesive': 'IsAdhesive',
    'gp.share_own_brand': 'IsOwnBrand', 'gp.north_africa': 'IsNorthAfrica', 'gp.cc_turkey': 'IsCcTurkey',
    'gp.cosmetics_ytd': 'IsCosmetics', 'gp.adhesive_ytd': 'IsAdhesive', 'gp.adhesive_suppliers_ytd': 'IsAdhesive',
};

/** The columns the KPI is computed from, in the order a reader checks them. */
export function leadingColumns(kpiKey: string, category: string): string[] {
    switch (category) {
        case 'gp': return ['GrossProfitEUR', 'SupplierName', 'IsKraton', ...(GP_FLAG[kpiKey] ? [GP_FLAG[kpiKey]] : []), 'CustomerName', 'PostingDate'];
        case 'crm':
            if (kpiKey === 'crm.open_gp' || kpiKey === 'crm.potential_gp_realization') return ['PotentialGpPerYear', 'DealName', 'StageLabel'];
            if (kpiKey === 'crm.avg_days_to_close_12m') return ['RelevantDays', 'ReachedTrialOrNegotiation', 'ActualCloseDate', 'DealName', 'IsWon'];
            if (kpiKey === 'crm.success_rate_12m') return ['IsWon', 'ReachedTrialOrNegotiation', 'ActualCloseDate', 'DealName'];
            if (kpiKey === 'crm.closed_rate_12m') return ['CreateDate', 'IsClosed', 'DealName'];
            return ['IsWon', 'CloseDate', 'DealName', 'DaysToClose'];
        case 'visits': return ['MeetingDate', 'Classification', 'ActivityClass', 'CustomerName'];
        case 'bab': return ['Amount', 'LineName', 'CompanyCode'];
        case 'marketdata': return [kpiKey === 'marketdata.modified_ytd' ? 'ModifiedAt' : 'CreatedAt', 'Name'];
        default: return [];
    }
}

/** The server's columns with the KPI's own first; every column stays (ND-05 a). */
export function orderColumns(columns: string[], kpiKey: string, category: string): string[] {
    const lead = leadingColumns(kpiKey, category).filter(c => columns.includes(c));
    return [...lead, ...columns.filter(c => !lead.includes(c))];
}

const VISIT_CLASS: Record<string, string> = {
    'visits.target_visits_ytd': 'Target visits (A, B and C)', 'visits.a_ytd': 'A visits', 'visits.b_ytd': 'B visits',
    'visits.c_ytd': 'C visits', 'visits.teleconferences_ytd': 'Teleconferences',
};

/**
 * What the rows are and how they add up to the tile, in English. The engine's text is German and
 * written for the developer; it stays the fallback for a KPI this list does not know.
 */
export function evidenceDescription(kpiKey: string, category: string, engineText: string): string {
    const byKey: Record<string, string> = {
        'crm.open_gp': 'Open deals in Industrial trial or Negotiation (pipeline Projects, created since 2022). The sum of Potential GP per year is the Open GP on the tile.',
        'crm.potential_gp_realization': 'Open deals in Industrial trial or Negotiation; the sum of Potential GP per year is the Open GP. Realization = 365 ÷ Avg Days × Success Rate × Open GP, with the Success Rate and Avg Days shown under Context.',
        'crm.success_rate_12m': 'Deals won or lost in the rolling 12 months, by the day they entered Won or Lost (not the close date). Only deals that reached Industrial trial or Negotiation count: Success Rate = won ÷ counted. The others are listed and counted as excluded.',
        'crm.avg_days_to_close_12m': 'Deals won or lost in the rolling 12 months, by the day they entered Won or Lost. "Days in relevant stages" is the time in Sample requested, Sample under test, Industrial trial and Negotiation. Only deals that reached Industrial trial or Negotiation with more than 0 such days count; the others are listed as excluded.',
        'crm.closed_rate_12m': 'Deals created in the rolling 12 months — the denominator of the Closed Rate; "closed of these" is its numerator.',
        'crm.won_deals_12m': 'Deals closed in the rolling 12 months (by close date); the won ones are the tile.',
        'crm.closed_deals_12m': 'Deals closed in the rolling 12 months (by close date); their number is the tile.',
        'gp.ytd': 'Sales lines in the year-to-date window; the sum of Gross profit (EUR) is the tile. The person is the customer\'s sales manager.',
        'gp.ex_kraton_ytd': 'Sales lines in the year-to-date window whose supplier brand is not Kraton (all categories; lines without a supplier count). The sum of Gross profit (EUR) is the tile. The person is the customer\'s sales manager.',
        'gp.adhesive_suppliers_ytd': 'Adhesive sales lines in the year-to-date window with a supplier brand (lines without a supplier and "Service / Overhead" left out). Suppliers = brands with a positive gross profit, per company, "Others" counting once — that number is the tile.',
        'marketdata.created_ytd': 'Market data records of the persons created in the year-to-date window; their number is the tile.',
        'marketdata.modified_ytd': 'Market data records of the persons last modified in the year-to-date window; their number is the tile.',
    };
    if (byKey[kpiKey]) return byKey[kpiKey];
    if (category === 'visits' && VISIT_CLASS[kpiKey]) {
        return `${VISIT_CLASS[kpiKey]} of the persons from 1 January to the as-of date — meetings held; the planned ones to the month end are shown separately on the tile. Their number is the tile.`;
    }
    if (category === 'gp' && kpiKey.startsWith('gp.share_')) {
        return 'Sales lines of the topic in the year-to-date window — the numerator of the share (the denominator is the gross profit of all lines). The person is the customer\'s sales manager.';
    }
    if (category === 'gp') return 'Sales lines of the topic in the year-to-date window; the sum of Gross profit (EUR) is the tile. The person is the customer\'s sales manager.';
    if (category === 'bab') return 'Frozen BAB year-to-date amounts of the month (latest freeze), each multiplied by the sign of its line.';
    return engineText;
}

/** Labels of the summary line above the rows. */
export const SUMMARY_LABEL: Record<string, string> = {
    rows: 'rows', openGp: 'Open GP (EUR)', meetings: 'meetings', grossProfitEUR: 'Gross profit (EUR)', won: 'won',
    closed: 'closed', counted: 'counted', excluded: 'excluded', avgDays: 'avg days', successRate: 'success rate',
    suppliers: 'suppliers', records: 'records', opened: 'created', closedOfThese: 'closed of these',
};

// =============================================================================================
// B2 — the node of an evidence view, the side panel's figures, the rows view's formatters
// =============================================================================================

/** Only a node with an evidence scope has rows: the endpoint knows no TOTAL (a multi-BU request). */
export function isEvidenceScope(scope: GroupScopeType): scope is EvidenceScopeType {
    return scope !== 'TOTAL';
}

/** What the page holds besides the node: the request context of the rows. */
export interface EvidenceContext {
    year: number;
    asOf: string;
    /** K3: the dashboard's BUs, sent along so the rows are those of the selected persons. */
    bus: string[];
    selection: KpiSelection | null;
}

/**
 * The request of the rows view: exactly the fields the evidence modal sends to `strategicKpisApi.evidence`
 * (`kpiKey, year, asOf, scopeType, scopeKey, bus, selection`), built in one place so the drill view cannot
 * drift from it. `kpiKey` is separate because the view can switch the KPI inside the same node.
 */
export function evidenceQuery(
    node: { kpiKey: string; scopeType: EvidenceScopeType; scopeKey: string },
    ctx: EvidenceContext,
    kpiKey: string = node.kpiKey,
): EvidenceQuery {
    return { kpiKey, year: ctx.year, asOf: ctx.asOf, scopeType: node.scopeType, scopeKey: node.scopeKey, bus: ctx.bus, selection: ctx.selection };
}

/**
 * The full node from what an address names (`dkpi`, `dscope`, `dkey`): the label and the `partial` flag are
 * read from the actuals payload — the same labels a click on that number would have carried. Without the
 * payload (still loading after a reload) or without a match the key stands in for the label.
 */
export function resolveEvidenceNode(
    data: ActualsResponse | null,
    ref: { kpiKey: string; scopeType: GroupScopeType; scopeKey: string },
): EvidenceNode {
    const node: EvidenceNode = { kpiKey: ref.kpiKey, scopeType: ref.scopeType, scopeKey: ref.scopeKey, label: ref.scopeKey };
    if (!data) return node;
    if (ref.scopeType === 'PERSON') {
        const p = data.persons.find(x => x.staffId === ref.scopeKey);
        if (p) node.label = p.fullName;
        return node;
    }
    const g = findGroup(data, ref.scopeType, ref.scopeKey);
    if (g) {
        node.label = groupLabel(g);
        if (g.partial) node.partial = true;
    }
    return node;
}

function findGroup(data: ActualsResponse, scopeType: GroupScopeType, scopeKey: string): GroupActuals | null {
    return data.groups.find(g => g.scopeType === scopeType && g.scopeKey === scopeKey) ?? null;
}

/** "All BUs", "Team Joaquin Xavier", "dit", "SPC" — same words as `scopeLabel` of the selectors. */
function groupLabel(g: Pick<GroupActuals, 'scopeType' | 'label'>): string {
    return g.scopeType === 'TOTAL' ? 'All BUs' : g.scopeType === 'TEAM' ? `Team ${g.label}` : g.label;
}

export type RowsAvailability = { ok: true } | { ok: false; reason: string };

/**
 * Whether «Show the rows» can lead anywhere: no TOTAL (no endpoint scope), no derived KPI (a calculation
 * of other KPIs), and BAB only for a whole company or BU — it has no person, team or selection level (the
 * engine answers 400 there). The reason is shown beside the disabled button.
 */
export function rowsAvailability(
    kpi: Pick<KpiCatalogueEntry, 'category' | 'groupAggregation'>,
    node: Pick<EvidenceNode, 'scopeType' | 'partial'>,
): RowsAvailability {
    if (node.scopeType === 'TOTAL') return { ok: false, reason: 'Rows open for one BU, company, team or person, not for the sum of several BUs.' };
    if (kpi.groupAggregation === 'derived') return { ok: false, reason: 'This KPI is calculated from other KPIs; it has no rows of its own.' };
    if (kpi.category === 'bab' && !((node.scopeType === 'COMPANY' || node.scopeType === 'BU') && !node.partial)) {
        return { ok: false, reason: 'BAB figures exist per company and for the BU only; there are no rows for a team, a person or a selection.' };
    }
    return { ok: true };
}

/** The KPIs the rows view can switch to inside one node: the modal's list (no derived, no mean), minus BAB where it has no rows. */
export function evidenceKpiOptions(kpis: KpiCatalogueEntry[], node: Pick<EvidenceNode, 'scopeType' | 'partial'>): KpiCatalogueEntry[] {
    return kpis.filter(k => k.groupAggregation !== 'mean' && rowsAvailability(k, node).ok);
}

// ------------------------------------------------------------------ the side panel's figures

export interface EvidenceSplitRow {
    /** The part as a node of its own: a click opens the panel for it. */
    node: EvidenceNode;
    label: string;
    sub: string | null;
    actualText: string;
    achievedText: string;
    light: Light;
}

export interface EvidenceSplit {
    kind: 'teams' | 'persons' | 'companies';
    /** "By team", "By person", "By company". */
    title: string;
    rows: EvidenceSplitRow[];
}

export interface EvidencePanelModel {
    kpi: KpiCatalogueEntry;
    /** The KPI with its unit, as everywhere ("Open GP (EUR)"). */
    title: string;
    /** "dit · definition v4". */
    sub: string;
    windowText: string;
    actualText: string;
    /** Why the actual is empty (engine reason, English); null when there is a value. */
    noDataText: string | null;
    /** Visits, definition v2: "+ 12 planned" — not part of the actual. */
    plannedText: string | null;
    hasTarget: boolean;
    /** Visits and GP amounts (answer 21): the YTD share; the others: the year target (the engine does not prorate them). */
    targetToDateText: string;
    /** "group target · vs full-year target". */
    targetBasisText: string | null;
    /** Visits and GP amounts: the year target next to the YTD share. */
    yearTargetText: string | null;
    /** A group target next to Σ of the person targets (decision 1): the Σ. */
    personsTargetText: string | null;
    achievedText: string;
    /** "of the year target, judged at the year end: no light before" for Market Data; null otherwise. */
    achievedNote: string | null;
    /** The word beside the light ("behind"); null where there is no light. */
    statusWord: string | null;
    light: Light;
    /** Prior year as a plain figure (never a difference, never coloured). */
    priorText: string;
    priorWindowText: string;
    /** K3: the node holds only the selected persons. */
    partialNote: string | null;
    split: EvidenceSplit | null;
    rows: RowsAvailability;
    definition: string;
    definitionVersion: number;
}

function personSub(p: PersonActuals): string {
    return `${p.companyCode}${p.isActive ? '' : ' · left'}`;
}

function byActualDesc<T extends { actual: number | null; label: string }>(a: T, b: T): number {
    if (a.actual === null && b.actual === null) return a.label.localeCompare(b.label);
    if (a.actual === null) return 1;
    if (b.actual === null) return -1;
    return b.actual - a.actual || a.label.localeCompare(b.label);
}

function splitRow(kpi: KpiCatalogueEntry, j: JudgedValue, atYearEnd: boolean, node: EvidenceNode, sub: string | null): EvidenceSplitRow & { actual: number | null } {
    return {
        node, label: node.label, sub, actual: j.actual,
        actualText: formatKpiValue(kpi.unit, j.actual),
        achievedText: formatAchievement(j.achievement, j.hasTarget),
        light: atYearEnd || !j.hasTarget ? 'none' : lightOf(j),
    };
}

/**
 * The parts a node divides into, for the KPI: the BU, the sum of BUs and a selection into teams; a team
 * and a company into persons; BAB (company and BU grain only) the BU and the sum into companies. A person
 * divides into nothing. Parts with neither a value nor a target are left out; the largest actual first.
 */
function splitOf(data: ActualsResponse, kpi: KpiCatalogueEntry, node: EvidenceNode, atYearEnd: boolean): EvidenceSplit | null {
    const make = (kind: EvidenceSplit['kind'], title: string, rows: (EvidenceSplitRow & { actual: number | null })[]): EvidenceSplit | null =>
        rows.length === 0 ? null : { kind, title, rows: [...rows].sort(byActualDesc).map(({ actual: _actual, ...r }) => r) };
    const groupNode = (g: GroupActuals): EvidenceNode => {
        const n: EvidenceNode = { kpiKey: kpi.kpiKey, scopeType: g.scopeType, scopeKey: g.scopeKey, label: groupLabel(g) };
        if (g.partial) n.partial = true;
        return n;
    };
    const personRows = (persons: PersonActuals[]) => persons.flatMap(p => {
        const j = judgePerson(p.values[kpi.kpiKey]);
        if (j.actual === null && !j.hasTarget) return [];
        return [splitRow(kpi, j, atYearEnd, { kpiKey: kpi.kpiKey, scopeType: 'PERSON', scopeKey: p.staffId, label: p.fullName }, personSub(p))];
    });
    const groupRows = (groups: GroupActuals[], sub: (g: GroupActuals) => string | null) => groups.flatMap(g => {
        const j = judgeGroup(kpi, g.values[kpi.kpiKey]);
        if (j.actual === null && !j.hasTarget) return [];
        return [splitRow(kpi, j, atYearEnd, groupNode(g), sub(g))];
    });

    const { scopeType, scopeKey } = node;
    if (scopeType === 'PERSON') return null;
    if (kpi.category === 'bab') {
        if (scopeType !== 'BU' && scopeType !== 'TOTAL') return null;
        const companies = data.groups.filter(g => g.scopeType === 'COMPANY' && (scopeType === 'TOTAL' || g.buCode === scopeKey));
        return make('companies', 'By company', groupRows(companies, g => (scopeType === 'TOTAL' ? g.buCode : null)));
    }
    if (scopeType === 'TEAM') {
        return make('persons', 'By person', personRows(data.persons.filter(p => (p.teamManagerStaffId ?? '') === scopeKey)));
    }
    if (scopeType === 'COMPANY') {
        const own = findGroup(data, 'COMPANY', scopeKey);
        const persons = data.persons.filter(p => p.companyCode === scopeKey && (!own?.buCode || p.buCode === own.buCode));
        return make('persons', 'By person', personRows(persons));
    }
    // BU, TOTAL, SELECTION: the teams of the persons in scope
    const inScope = scopeType === 'BU' ? data.persons.filter(p => p.buCode === scopeKey) : data.persons;
    const managers = new Set(inScope.map(p => p.teamManagerStaffId ?? ''));
    const teams = data.groups.filter(g => g.scopeType === 'TEAM' && managers.has(g.scopeKey));
    return make('teams', 'By team', groupRows(teams, () => null));
}

/**
 * Everything the evidence panel shows for a node, from the actuals payload alone (E9: no request). The
 * figures are the ones the steered rows, the ranking and the matrix show for the same node — the same
 * selectors (`judgeGroup` / `judgePerson`) and formatters — only arranged for a 400 px column. Null when
 * the KPI or the node is not in the payload (e.g. a person the selection leaves out).
 */
export function evidencePanelModel(data: ActualsResponse, node: EvidenceNode): EvidencePanelModel | null {
    const kpi = data.kpis.find(k => k.kpiKey === node.kpiKey);
    if (!kpi) return null;
    const person = node.scopeType === 'PERSON' ? data.persons.find(p => p.staffId === node.scopeKey) ?? null : null;
    const group = node.scopeType === 'PERSON' ? null : findGroup(data, node.scopeType, node.scopeKey);
    if (!person && !group) return null;

    const j: JudgedValue = person ? judgePerson(person.values[kpi.kpiKey]) : judgeGroup(kpi, group!.values[kpi.kpiKey]);
    const atYearEnd = paceAtYearEnd(kpi, data.windows.yearFraction);
    const hasPace = j.hasTarget && j.achievement !== null && Number.isFinite(j.achievement);
    const light: Light = atYearEnd || !hasPace ? 'none' : lightOf(j);
    const source = person ? 'person target' : j.hasGroupTarget ? 'group target' : 'Σ person targets';
    const isVisits = kpi.category === 'visits';
    const partial = node.partial === true || group?.partial === true;

    return {
        kpi,
        title: kpiLabel(kpi),
        sub: `${node.label} · definition v${kpi.definitionVersion}`,
        windowText: windowText(kpi, kpi.window),
        actualText: j.actual === null ? 'No data' : formatKpiValue(kpi.unit, j.actual),
        noDataText: j.actual === null ? noDataText(j.noData) : null,
        plannedText: isVisits && j.planned !== undefined && j.planned > 0 ? `+ ${j.planned} planned` : null,
        hasTarget: j.hasTarget,
        targetToDateText: j.hasTarget ? formatKpiValue(kpi.unit, j.targetYtd ?? j.target) : 'No target',
        targetBasisText: j.hasTarget ? `${source} · ${paceBasisText(kpi)}` : null,
        yearTargetText: j.hasTarget && judgedOnYtdShare(kpi) ? formatKpiValue(kpi.unit, j.target) : null,
        personsTargetText: !person && j.hasGroupTarget && j.personsTarget !== null ? formatKpiValue(kpi.unit, j.personsTarget) : null,
        achievedText: formatAchievement(j.achievement, j.hasTarget),
        achievedNote: atYearEnd && hasPace ? 'of the year target; judged at the year end, so no light before' : null,
        statusWord: light === 'none' ? null : STATUS_LABEL[light],
        light,
        priorText: j.prior === null ? (kpi.windowType === 'POINT' ? 'no status' : '—') : formatKpiValue(kpi.unit, j.prior),
        priorWindowText: windowText(kpi, kpi.priorWindow),
        partialNote: partial ? 'Only the persons of the current selection count here.' : null,
        split: splitOf(data, kpi, node, atYearEnd),
        rows: rowsAvailability(kpi, { scopeType: node.scopeType, partial }),
        definition: kpi.definitionText,
        definitionVersion: kpi.definitionVersion,
    };
}

// ------------------------------------------------------------------ the rows view's formatters

/** The label of a summary key ("meetings", "avg days", "Open GP (EUR)"). */
export function summaryLabel(k: string): string {
    return SUMMARY_LABEL[k] ?? k.replace(/([A-Z])/g, ' $1').toLowerCase();
}

/** A summary value in its unit — counts as integers, shares as %, amounts in EUR (the modal's rule, unchanged). */
export function summaryValue(k: string, v: number | null, unit: string | undefined): string {
    if (v === null) return '—';
    if (/rows|meetings|count|won|closed|deals|excluded|suppliers|records|opened/i.test(k)) return v.toLocaleString('de-CH', { maximumFractionDigits: 0 });
    if (k === 'successRate') return formatKpiValue('pct', v);
    if (k === 'avgDays') return formatKpiValue('days', v);
    if (/eur|gp|amount/i.test(k)) return formatKpiValue('amount', v);
    return formatKpiValue(unit ?? 'count', v);
}

/** A HubSpot link column (`DealUrl`, `MeetingURL`). */
export function isEvidenceUrlColumn(col: string): boolean {
    return /url$/i.test(col);
}

/**
 * A cell as text, as the modal writes it: «yes»/«no» for flags, identifiers untouched, amounts with two
 * decimals, days with one, dates as dd.mm.yyyy (a time column keeps its clock). Null = empty (shown as a
 * dash). A URL column is not text: the view draws a link for it.
 */
export function evidenceCellText(col: string, v: unknown): string | null {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'boolean') return v ? 'yes' : 'no';
    if (isIdentifierColumn(col)) return String(v);
    if (typeof v === 'number') {
        if (isAmountColumn(col)) return v.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (isDaysColumn(col)) return v.toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        return v.toLocaleString('de-CH', { maximumFractionDigits: Number.isInteger(v) ? 0 : 2 });
    }
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
        // Date-only columns arrive as midnight UTC; a time column keeps its clock.
        return /Time$/.test(col) ? formatDateTimeCH(v) : formatDate(v);
    }
    return String(v);
}

export interface EvidenceSort {
    key: string | null;
    direction: 'asc' | 'desc';
}

/** A new column sorts text ascending and numbers descending; the same column flips (the table standard). */
export function nextEvidenceSort(current: EvidenceSort, key: string, numeric: boolean): EvidenceSort {
    if (current.key === key) return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    return { key, direction: numeric ? 'desc' : 'asc' };
}

/** The whole row set sorted by one column, empty values last in either direction (the modal's sort, unchanged). */
export function sortEvidenceRows(rows: Record<string, unknown>[], sort: EvidenceSort): Record<string, unknown>[] {
    if (!sort.key) return rows;
    const key = sort.key;
    return [...rows].sort((a, b) => {
        const av = a[key] as string | number | boolean | null | undefined;
        const bv = b[key] as string | number | boolean | null | undefined;
        const aNil = av === null || av === undefined || av === '';
        const bNil = bv === null || bv === undefined || bv === '';
        if (aNil && bNil) return 0;
        if (aNil) return 1;
        if (bNil) return -1;
        const base = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return base * (sort.direction === 'asc' ? 1 : -1);
    });
}

/** The columns right-aligned: numbers that are not identifiers. */
export function numericEvidenceColumns(columns: string[], rows: Record<string, unknown>[]): Set<string> {
    return new Set(columns.filter(c => !isIdentifierColumn(c) && rows.some(r => typeof r[c] === 'number')));
}

/** The rows view shows the rows in pages (a person's GP evidence can be 5 000 × 22 cells); sorting covers all of them. */
export const EVIDENCE_PAGE = 500;
